"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { ChevronLeft, ChevronRight, Home } from "lucide-react";

/* page titles for the dashboard topbar (matched by prefix, longest first) */
const TITLES: { p: string; ar: string; en: string }[] = [
  { p: "/dashboard/properties/new", ar: "إضافة عقار", en: "Add property" },
  { p: "/dashboard/properties/edit", ar: "تعديل عقار", en: "Edit property" },
  { p: "/dashboard/properties", ar: "عقاراتي", en: "My properties" },
  { p: "/dashboard/transactions", ar: "المعاملات", en: "Transactions" },
  { p: "/dashboard/account", ar: "حالة الحساب", en: "Account status" },
  { p: "/dashboard/documents", ar: "المستندات والتراخيص", en: "Documents & licences" },
  { p: "/dashboard/projects", ar: "المشاريع العقارية", en: "Projects" },
  { p: "/dashboard/3d-photography", ar: "التصوير ثلاثي الأبعاد", en: "3D photography" },
  { p: "/dashboard/ads", ar: "الإعلانات", en: "Advertisements" },
  { p: "/dashboard/profile", ar: "الملف الشخصي", en: "Profile" },
  { p: "/dashboard/verification-account", ar: "توثيق الحساب", en: "Verification" },
  { p: "/dashboard", ar: "لوحة التحكم", en: "Dashboard" },
  { p: "/bank/dashboard", ar: "لوحة تحكم البنك", en: "Bank dashboard" },
  { p: "/bank/mortgage-programs", ar: "برامج التمويل", en: "Mortgage programs" },
  { p: "/bank/mortgage-applications", ar: "طلبات التمويل", en: "Mortgage applications" },
  { p: "/bank/payables", ar: "المبالغ المستحقة", en: "Payables" },
  { p: "/bank/auctions", ar: "المزادات", en: "Auctions" },
  { p: "/bank/details", ar: "بيانات البنك", en: "Bank details" },
  { p: "/bank", ar: "بوابة البنك", en: "Bank portal" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const locale = useLocale();
  const ar = locale === "ar";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDashboard = pathname.includes("/dashboard") || pathname.includes("/bank");
  const isAuth = pathname.includes("/auth");
  if (isAuth) return null;

  const clean = pathname.replace(`/${locale}`, "");
  const match = TITLES.find((t) => clean.startsWith(t.p));
  const title = match ? (ar ? match.ar : match.en) : ar ? "لوحة التحكم" : "Dashboard";
  const isRoot = clean === "/dashboard" || clean === "/bank/dashboard";
  const Crumb = ar ? ChevronLeft : ChevronRight;

  /* ── Dashboard topbar ─────────────────────────────────── */
  if (isDashboard) {
    return (
      <header
        className={`sticky top-0 z-40 w-full border-b transition-all duration-300 ${
          scrolled ? "border-slate-200/70 bg-white/80 shadow-[0_10px_30px_-20px_rgba(15,23,42,0.25)] backdrop-blur-xl" : "border-transparent bg-[#f6f7fb]"
        }`}
      >
        <div className="flex items-center justify-between px-4 py-3.5 md:px-8">
          <div className="min-w-0">
            <nav className="flex items-center gap-1 text-[11px] font-bold text-slate-400">
              <Link href={clean.startsWith("/bank") ? "/bank/dashboard" : "/dashboard"} className="flex items-center gap-1 hover:text-[#0d3589]">
                <Home size={12} /> {ar ? "الرئيسية" : "Home"}
              </Link>
              {!isRoot && (
                <>
                  <Crumb size={12} />
                  <span className="truncate text-slate-600">{title}</span>
                </>
              )}
            </nav>
            <h3 className="mt-0.5 truncate text-xl font-black tracking-tight text-slate-900 md:text-2xl">{title}</h3>
          </div>

          <div className="flex items-center gap-2">
            <span className="hidden items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[11px] font-black text-emerald-600 md:inline-flex">
              <span className="size-1.5 animate-pulse rounded-full bg-emerald-500" /> {ar ? "مباشر" : "Live"}
            </span>
            <NotificationBell />
            <LanguageSwitcher />
          </div>
        </div>
      </header>
    );
  }

  /* ── Public site header (unchanged behaviour) ─────────── */
  return (
    <header
      className={`w-full z-50 transition-all py-2 duration-300 ${
        scrolled ? "bg-[#080A14]/90 backdrop-blur-lg" : "bg-transparent"
      }`}
    >
      <div className="md:container px-4 mx-auto py-3 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2">
          <Image src="/logo.png" alt="Logo" width={110} height={50} priority className="object-contain" />
        </Link>
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
